/**
 * Light/dark toggle.
 *
 * Writes `data-theme` on <html>, which is what the `dark:` variant is wired to
 * in _v2/css/src/tokens.css. It is deliberately NOT wired to
 * prefers-color-scheme: the site ships an explicit toggle, and a user who
 * picked light must keep light even on a machine set to dark.
 *
 * The OS preference is only the DEFAULT — used when the visitor has never
 * chosen. Once they choose, localStorage wins forever.
 *
 * NOTE ON THE FLASH. Applying the theme from here means one frame of the
 * default palette before the class lands. The fix is a two-line inline script
 * in Layouts/head/global-head.ctp that reads localStorage before first paint;
 * that script and this module must agree on both the key and the attribute.
 */
import { qsa, on } from './dom.js';

const KEY = 'tt-theme';
const ATTR = 'data-theme';

const systemPrefersDark = () => window.matchMedia('(prefers-color-scheme: dark)').matches;

function stored() {
  try {
    return localStorage.getItem(KEY);
  } catch {
    // Private mode and some embedded webviews throw on any localStorage
    // access. The toggle must still work for the current page.
    return null;
  }
}

function persist(theme) {
  try {
    localStorage.setItem(KEY, theme);
  } catch {
    // See above — losing persistence is acceptable, throwing is not.
  }
}

function apply(theme) {
  document.documentElement.setAttribute(ATTR, theme);
  // Tell every toggle button what the CURRENT state is, so a screen reader
  // announces the state rather than the action.
  for (const button of qsa('[data-theme-toggle]')) {
    button.setAttribute('aria-pressed', String(theme === 'dark'));
  }
}

export function current() {
  return document.documentElement.getAttribute(ATTR) === 'dark' ? 'dark' : 'light';
}

export function setupTheme() {
  apply(stored() || (systemPrefersDark() ? 'dark' : 'light'));

  const toggles = qsa('[data-theme-toggle]');
  if (!toggles.length) return;

  for (const button of toggles) {
    on(button, 'click', () => {
      const next = current() === 'dark' ? 'light' : 'dark';
      apply(next);
      persist(next);
    });
  }

  // Follow the OS only while the visitor has never chosen.
  on(window.matchMedia('(prefers-color-scheme: dark)'), 'change', (event) => {
    if (stored()) return;
    apply(event.matches ? 'dark' : 'light');
  });
}
